import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Trash2, CheckCircle2, XCircle, Loader2, Pencil } from "lucide-react";
import { api, resolveMedia } from "../../lib/api";
import { errMsg, useCategories, CategorySelect } from "./shared";

export default function GalleryManager() {
  const [cats] = useCategories();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [queue, setQueue] = useState([]);
  const [uploadCat, setUploadCat] = useState("other");
  const [selected, setSelected] = useState(new Set());
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/gallery").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const addFiles = (files) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!arr.length) {
      toast.error("केवल इमेज फाइल चुनें (jpg, png, webp)");
      return;
    }
    setQueue((q) => [
      ...q,
      ...arr.map((f) => ({
        file: f,
        name: f.name,
        caption: f.name.replace(/\.[^.]+$/, ""),
        preview: URL.createObjectURL(f),
        progress: 0,
        status: "pending",
        url: null,
      })),
    ]);
  };

  const startUpload = async () => {
    if (!uploadCat) {
      toast.error("कृपया श्रेणी चुनें");
      return;
    }
    for (let idx = 0; idx < queue.length; idx++) {
      const item = queue[idx];
      if (item.status !== "pending") continue;
      setQueue((q) => q.map((x, i) => (i === idx ? { ...x, status: "uploading" } : x)));
      try {
        const fd = new FormData();
        fd.append("file", item.file);
        const r = await api.post(`/admin/upload?context=gallery&category=${uploadCat}`, fd, {
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded * 100) / (e.total || 1));
            setQueue((q) => q.map((x, i) => (i === idx ? { ...x, progress: pct } : x)));
          },
        });
        await api.post("/admin/gallery", {
          url: r.data.url,
          caption_hi: item.caption || item.name,
          caption_en: "",
          category: uploadCat,
        });
        setQueue((q) => q.map((x, i) => (i === idx ? { ...x, status: "done", url: r.data.url } : x)));
      } catch (err) {
        setQueue((q) => q.map((x, i) => (i === idx ? { ...x, status: "error" } : x)));
        toast.error(`${item.name}: ${errMsg(err)}`);
      }
    }
    toast.success("अपलोड पूर्ण — फोटो गैलरी में दिखने लगी हैं");
    load();
  };

  const clearDone = () => setQueue((q) => q.filter((x) => x.status !== "done"));
  const removeFromQueue = (idx) => setQueue((q) => q.filter((_, i) => i !== idx));

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const deleteOne = async (id) => {
    if (!window.confirm("क्या आप यह फोटो हटाना चाहते हैं?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const [editItem, setEditItem] = useState(null);
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/gallery/${editItem.id}`, { caption_hi: editItem.caption_hi, caption_en: editItem.caption_en || "", category: editItem.category });
      toast.success("फोटो जानकारी अपडेट हुई");
      setEditItem(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`क्या आप ${selected.size} चयनित फोटो हटाना चाहते हैं?`)) return;
    for (const id of selected) {
      try { await api.delete(`/admin/gallery/${id}`); } catch {}
    }
    toast.success("चयनित फोटो हटाई गईं");
    setSelected(new Set());
    load();
  };

  const shown = filter ? items.filter((i) => i.category === filter) : items;

  return (
    <div>
      <div className="bg-white rounded-2xl border border-ngo-deep/10 p-6 mb-10">
        <h3 className="font-heading font-bold text-lg text-ngo-ink mb-4">नई फोटो अपलोड करें (एक साथ कई)</h3>
        <div className="grid sm:grid-cols-[220px_1fr] gap-4 items-start">
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">श्रेणी *</label>
            <CategorySelect testid="gallery-upload-category" value={uploadCat} onChange={setUploadCat} />
          </div>
          <div
            data-testid="gallery-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            className={`rounded-2xl border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors duration-200 ${dragOver ? "border-ngo-leaf bg-ngo-mint" : "border-ngo-leaf/40 hover:bg-ngo-mint/60"}`}
          >
            <UploadCloud size={34} className="mx-auto text-ngo-deep/60" />
            <p className="mt-3 text-sm font-semibold text-ngo-deep">फोटो यहां खींचकर छोड़ें या क्लिक करके चुनें</p>
            <p className="mt-1 text-xs text-ngo-ink/50">एक साथ कई फोटो चुन सकते हैं • jpg, png, webp • अधिकतम 8MB प्रति फोटो</p>
            <input ref={fileRef} data-testid="gallery-file-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          </div>
        </div>

        {queue.length > 0 && (
          <div className="mt-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {queue.map((q, i) => (
                <div key={i} className="relative rounded-xl border border-ngo-deep/10 overflow-hidden bg-ngo-cream">
                  <img src={q.preview} alt={q.name} className="w-full h-28 object-cover" />
                  <div className="p-2">
                    <input
                      data-testid={`queue-caption-${i}`}
                      className="w-full text-xs rounded-lg border border-ngo-deep/15 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-ngo-leaf/50"
                      value={q.caption}
                      placeholder="फोटो शीर्षक"
                      onChange={(e) => setQueue((prev) => prev.map((x, xi) => (xi === i ? { ...x, caption: e.target.value } : x)))}
                    />
                  </div>
                  {q.status === "uploading" && (
                    <div className="absolute inset-0 bg-ngo-forest/70 flex flex-col items-center justify-center text-white">
                      <Loader2 size={22} className="animate-spin" />
                      <span className="text-xs mt-2 font-bold">{q.progress}%</span>
                    </div>
                  )}
                  {q.status === "done" && <div className="absolute top-2 left-2 text-green-600 bg-white rounded-full"><CheckCircle2 size={20} /></div>}
                  {q.status === "error" && <div className="absolute top-2 left-2 text-red-600 bg-white rounded-full"><XCircle size={20} /></div>}
                  <button onClick={() => removeFromQueue(i)} aria-label="हटाएं" className="absolute top-2 right-2 w-6 h-6 rounded-full bg-ngo-forest/80 text-white text-xs flex items-center justify-center">✕</button>
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <button data-testid="gallery-start-upload" onClick={startUpload} disabled={!queue.some((q) => q.status === "pending")} className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest disabled:opacity-50 transition-colors duration-300">
                <UploadCloud size={17} /> अपलोड शुरू करें ({queue.filter((q) => q.status === "pending").length})
              </button>
              <button onClick={clearDone} className="px-6 py-3 rounded-full border border-ngo-deep/20 text-ngo-deep text-sm font-semibold hover:bg-ngo-mint transition-colors duration-200">पूर्ण हुई फोटो साफ करें</button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select data-testid="gallery-admin-filter" className="w-auto rounded-xl border border-ngo-deep/20 bg-white px-4 py-2.5 text-sm text-ngo-ink" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">सभी श्रेणियां ({items.length})</option>
          {cats.map((c) => (
            <option key={c.slug} value={c.slug}>{c.name_hi} ({items.filter((i) => i.category === c.slug).length})</option>
          ))}
        </select>
        {selected.size > 0 && (
          <button data-testid="gallery-delete-selected" onClick={deleteSelected} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors duration-200">
            <Trash2 size={14} /> चयनित हटाएं ({selected.size})
          </button>
        )}
      </div>

      {editItem && (
        <form data-testid="gallery-edit-form" onSubmit={saveEdit} className="bg-ngo-mint/70 border border-ngo-leaf/30 rounded-2xl p-5 mb-6 grid sm:grid-cols-[1fr_220px_auto] gap-3 items-end">
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">फोटो शीर्षक *</label>
            <input data-testid="gallery-edit-caption" required className="w-full rounded-xl border border-ngo-deep/20 bg-white px-4 py-2.5 text-sm text-ngo-ink focus:outline-none focus:ring-2 focus:ring-ngo-leaf/50" value={editItem.caption_hi} onChange={(e) => setEditItem({ ...editItem, caption_hi: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">श्रेणी</label>
            <CategorySelect testid="gallery-edit-category" value={editItem.category} onChange={(v) => setEditItem({ ...editItem, category: v })} />
          </div>
          <div className="flex gap-2">
            <button data-testid="gallery-edit-save" className="px-5 py-2.5 rounded-full bg-ngo-deep text-white text-sm font-bold">सहेजें</button>
            <button type="button" onClick={() => setEditItem(null)} className="px-4 py-2.5 rounded-full border border-ngo-deep/20 text-ngo-deep text-sm font-semibold">रद्द</button>
          </div>
        </form>
      )}

      <div data-testid="gallery-admin-list" className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {shown.map((it) => (
          <div key={it.id} className={`relative group rounded-xl overflow-hidden border-2 ${selected.has(it.id) ? "border-ngo-gold" : "border-ngo-deep/10"}`}>
            <img src={resolveMedia(it.url)} alt={it.caption_hi} loading="lazy" className="w-full h-32 object-cover" />
            <p className="absolute inset-x-0 bottom-0 bg-ngo-forest/85 text-ngo-cream text-[11px] px-2.5 py-1.5 truncate">{it.caption_hi}</p>
            <input
              type="checkbox"
              data-testid={`gallery-select-${it.id}`}
              checked={selected.has(it.id)}
              onChange={() => toggleSelect(it.id)}
              className="absolute top-2 left-2 w-4.5 h-4.5 w-[18px] h-[18px] accent-ngo-deep"
              aria-label="चुनें"
            />
            <button
              data-testid={`gallery-edit-${it.id}`}
              onClick={() => { setEditItem(it); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              aria-label="संपादित करें"
              className="absolute top-2 right-10 w-7 h-7 rounded-full bg-ngo-deep text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <Pencil size={12} />
            </button>
            <button
              data-testid={`gallery-delete-${it.id}`}
              onClick={() => deleteOne(it.id)}
              aria-label="हटाएं"
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
      {shown.length === 0 && <p data-testid="gallery-admin-empty" className="text-center text-ngo-ink/50 py-14">इस श्रेणी में कोई फोटो नहीं है।</p>}
    </div>
  );
}
